/*
 * Copyright (c) 2025 Frayo and HomeChef360.
 */

import React, { useState, useEffect } from "react";
import { OrderProps } from "../modules/orders/core/_models";
import { useFormattedUtils } from "../component/dateUtils";
import { useAuth } from "../auth";
import { listBankData } from "../modules/settlements/core/_requests";
import { useNavigate } from "react-router-dom";

interface OrderModalProps {
  showOrderModal: boolean;
  order: OrderProps[];
  handleOrderStatusChange: (order: OrderProps, action: string, reason?: string) => void;
  stopNotificationSound: () => void;
}

const OrderModal: React.FC<OrderModalProps> = ({
  showOrderModal,
  order,
  handleOrderStatusChange,
  stopNotificationSound,
}) => {
  const { formatDateUS, formatTimeUS } = useFormattedUtils();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // UI states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderProps | null>(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [preparingOrders, setPreparingOrders] = useState<Record<string, boolean>>({});
  const [showPreparationConfirm, setShowPreparationConfirm] = useState<Record<string, boolean>>({});
  const [currentPreparingOrder, setCurrentPreparingOrder] = useState<OrderProps | null>(null);
  const [processedOrders, setProcessedOrders] = useState<Set<string>>(new Set());
  const [showScheduledReminder, setShowScheduledReminder] = useState<boolean>(false);
  const [scheduledReminderOrder, setScheduledReminderOrder] = useState<OrderProps | null>(null);
  const [scheduledReminderMessage, setScheduledReminderMessage] = useState<string>("");
  const [preparationMessage, setPreparationMessage] = useState<string>("");
  const [showBankAlert, setShowBankAlert] = useState(false);
  const [hasCheckedBank, setHasCheckedBank] = useState(false);

  const rejectionReasons = [
    "Preparation not started",
    "Ingredient not available",
    "Too many orders",
    "Kitchen closed",
    "Dish not available",
    "Technical issue",
    "Other",
  ];

  /***********************************************************
   * ⭐ 1) ACCEPT ORDER (No timer — backend handles logic)
   ***********************************************************/
  const handleAcceptOrder = (item: OrderProps) => {
    handleOrderStatusChange(item, "Accept Order");
    stopNotificationSound();

    const socket = (window as any).socket;
    if (socket) {
      // backend sets acceptedTime and starts preparation cycle
      socket.emit("chefPreparationYes", {
        orderId: item.uuid,
        chefId: item.chefId,
      });
    }
  };
  useEffect(() => {
    const socket = (window as any).socket;
    if (!socket) return;

    const reminderHandler = ({ orderId, message }: any) => {
      const foundOrder = order.find((o) => o.uuid === orderId);
      if (!foundOrder) return;

      stopNotificationSound();
      setScheduledReminderOrder(foundOrder);
      setScheduledReminderMessage(message);
      setShowScheduledReminder(true); // OPEN Reminder popup
    };

    socket.on("scheduledReminder", reminderHandler);

    return () => socket.off("scheduledReminder", reminderHandler);
  }, [order]);

  /***********************************************************
   * ⭐ 2) BACKEND ASKS POPUP → askPreparation
   ***********************************************************/
  useEffect(() => {
    const socket = (window as any).socket;
    if (!socket) return;

    const onAsk = ({
      orderId,
      message,
    }: {
      orderId: string;
      message: string;
    }) => {
      console.log("📥 askPreparation received:", { orderId, message });

      const found = order.find((o) => o.uuid === orderId);
      if (!found) return;

      setCurrentPreparingOrder(found);
      setPreparationMessage(message); // ✅ STORE MESSAGE
      setShowPreparationConfirm((prev) => ({
        ...prev,
        [orderId]: true,
      }));
    };

    socket.on("askPreparation", onAsk);
    return () => socket.off("askPreparation", onAsk);
  }, [order]);

  useEffect(() => {
    const socket = (window as any).socket;
    if (!socket) return;

    const onStatusChanged = (data: {
      orderId: string;
      orderstatusId: number;
      reason?: string;
    }) => {



      // // ❗ Prevent duplicates
      // if (processedOrders.has(data.orderId)) {
      //   console.log("Duplicate ignored:", data.orderId);
      //   return;
      // }

      setProcessedOrders(prev => new Set(prev).add(data.orderId));

      stopNotificationSound();
      setShowPreparationConfirm({});
      setPreparingOrders({});
      if (data.orderstatusId === 7) {
        console.log("Auto cancelled:", data.orderId);
        // Just update UI states
      }


      // if (data.orderstatusId === 7) {
      //   handleOrderStatusChange(
      //     { uuid: data.orderId } as any,
      //     "Cancel Order",
      //     data.reason || "Auto cancelled by system"
      //   );
      // }
    };

    socket.on("orderStatusChanged", onStatusChanged);

    return () => socket.off("orderStatusChanged", onStatusChanged);
  }, [processedOrders]);

  useEffect(() => {
    Object.keys(preparingOrders).forEach((oid) => {
      if (preparingOrders[oid]) {
        // Hide after 5 seconds
        setTimeout(() => {
          setPreparingOrders((prev) => {
            const updated = { ...prev };
            delete updated[oid];   // Remove this preparing message
            return updated;
          });
        }, 5000);
      }
    });
  }, [preparingOrders]);

  /***********************************************************
   * ⭐ 3) ON PAGE REFRESH → checkPreparationStatus
   ***********************************************************/
  useEffect(() => {
    const socket = (window as any).socket;
    if (!socket) return;

    order.forEach((item) => {
      if (item.orderstatusId === 3) {
        socket.emit(
          "checkPreparationStatus",
          { orderId: item.uuid },
          (res: any) => {
            if (res.show) {
              setCurrentPreparingOrder(item);
              setShowPreparationConfirm((prev) => ({
                ...prev,
                [item.uuid]: true,
              }));
            }
          }
        );
      }
    });
  }, [order]);

  /***********************************************************
   * ⭐ 3.5) CHECK BANK DETAILS ON NEW ORDER
   ***********************************************************/
  useEffect(() => {
    const hasNewOrder = order.some((o) => o.orderstatusId === 1);

    if (showOrderModal && hasNewOrder && currentUser?.uuid && !hasCheckedBank) {
      listBankData(currentUser.uuid)
        .then((res) => {
          if (!res?.data || res.data.length === 0) {
            setShowBankAlert(true);
          }
          setHasCheckedBank(true);
        })
        .catch((err) => console.error("Error checking bank details:", err));
    }
  }, [showOrderModal, order, currentUser, hasCheckedBank]);

  /***********************************************************
   * ⭐ 4) CHEF PRESSES YES / NO → send socket event
   ***********************************************************/
  const handlePreparationResponse = (order: OrderProps, hasStarted: boolean) => {
    const socket = (window as any).socket;
    if (!socket) return;

    socket.emit("chefPreparationResponse", {
      orderId: order.uuid,
      chefId: order.chefId,
      hasStarted, // 👈 TRUE = Yes, FALSE = No
    });

    if (hasStarted) {
      setPreparingOrders((prev) => ({
        ...prev,
        [order.uuid]: true,
      }));
    } else {
      handleOrderStatusChange(order, "Cancel Order", "Preparation not started");
      stopNotificationSound();
    }

    setShowPreparationConfirm((prev) => ({
      ...prev,
      [order.uuid]: false,
    }));

    setCurrentPreparingOrder(null);
  };

  /***********************************************************
   * ⭐ 5) Reject Order
   ***********************************************************/
  const handleRejectClick = (order: OrderProps) => {
    setSelectedOrder(order);
    setShowRejectModal(true);
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setSelectedOrder(null);
    setSelectedReason("");
  };

  const handleRejectConfirm = () => {
    if (selectedOrder && selectedReason.trim()) {
      handleOrderStatusChange(selectedOrder, "Cancel Order", selectedReason);
      stopNotificationSound();
      setShowRejectModal(false);
      setSelectedOrder(null);
      setSelectedReason("");
    }
  };

  /***********************************************************
   *  UI Rendering (UNCHANGED)
   ***********************************************************/
  return (
    <div>

      {/* New Order Popup */}
      {showOrderModal && (
        <>
          {order.map((item, index) => {
            if (item.orderstatusId !== 1) return null;

            return (
              <div className="modal fade show new-order d-block" key={index}>
                {/* Overlay */}
                <div className="new-order-overlay position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"></div>

                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">

                    {/* Header */}
                    <div className="modal-header flex-column align-items-start bg-light border-0 p-4 pb-2">
                      <h2 className="modal-title fw-bold text-dark">New Order</h2>

                      {item.order_type === 3 && (
                        <div className="mt-2 bg-success bg-opacity-10 rounded-pill px-3 py-1">
                          <small className="text-success fw-semibold">Scheduled</small>
                          <small className="text-dark d-block">
                            {formatDateUS(item?.scheduled_date)} •{" "}
                            {formatTimeUS(
                              item?.scheduled_date && item?.scheduled_time
                                ? `${item.scheduled_date.split("T")[0]}T${item.scheduled_time}`
                                : item?.scheduled_date
                            )}
                          </small>
                        </div>
                      )}

                      <div className="order-detail mt-3 w-100 d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="text-primary mb-0 fw-semibold">Order {item?.orderId}</h5>
                          <span className="tiny text-muted">
                            {formatDateUS(item?.orderDate)} • {formatTimeUS(item?.orderDate)}
                          </span>
                        </div>

                        <div className="order-by text-end">
                          <small className="fw-medium text-secondary">
                            Order by: <span className="fw-semibold">{item.user?.name}</span>
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 pt-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {item.orderdetails.map((d, i2) => (
                        <div
                          key={i2}
                          className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3"
                        >
                          <div className="d-flex align-items-center">
                            <img
                              src={d.dish?.dishImage}
                              className="img-fluid rounded shadow-sm me-3"
                              style={{ width: 55, height: 55, objectFit: "cover" }}
                            />
                            <p className="mb-0">
                              <strong>{d.quantity}</strong> × {d.dish?.name}
                            </p>
                          </div>

                          <strong className="text-dark">
                            ${(d.quantity * d.amount).toFixed(2)}
                          </strong>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-0 d-flex justify-content-between p-4">
                      <button
                        className="btn btn-outline-danger rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => handleRejectClick(item)}
                      >
                        Reject
                      </button>

                      <button
                        className="btn btn-primary rounded-pill px-4 py-2 fw-semibold shadow-sm"
                        onClick={() => handleAcceptOrder(item)}
                      >
                        Accept
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Preparing Notification */}
      {Object.keys(preparingOrders).map((oid) => (
        preparingOrders[oid] && (
          <div
            key={oid}
            className="shadow-lg rounded p-3 bg-primary text-white"
            style={{
              position: "fixed",
              top: `${20 + Object.keys(preparingOrders).indexOf(oid) * 70}px`,
              right: "20px",
              zIndex: 9999,
            }}
          >
            <strong>
              Order {order.find(o => o.uuid === oid)?.orderId || oid}
            </strong>
            &nbsp; is being prepared...
          </div>
        )
      ))}


      {/* Preparation Confirmation Popup */}
      {currentPreparingOrder && showPreparationConfirm[currentPreparingOrder.uuid] && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-4 text-center">

              <h5 className="fw-bold text-primary">Order Preparation</h5>
              {/* <p>Have you started preparing <strong>Order {currentPreparingOrder.orderId}</strong>?</p> */}
              <p>
                {preparationMessage ||
                  `Have you started preparing Order ${currentPreparingOrder.orderId}?`}
              </p>

              <div className="d-flex justify-content-center gap-3 mt-3">
                <button className="btn btn-outline-secondary px-4"
                  onClick={() => handlePreparationResponse(currentPreparingOrder, false)}>
                  No
                </button>

                <button className="btn btn-success px-4"
                  onClick={() => handlePreparationResponse(currentPreparingOrder, true)}>
                  Yes
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-3">

              <h5>Reject Order</h5>

              <label className="mt-2 fw-semibold">Select Reason</label>
              <select
                className="form-select"
                value={selectedReason || "Other"}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedReason(v === "Other" ? "" : v);
                }}
              >
                <option value="Other">Other</option>
                {rejectionReasons.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              {(selectedReason === "" || !rejectionReasons.includes(selectedReason)) && (
                <textarea
                  className="form-control mt-3"
                  placeholder="Write reason..."
                  rows={3}
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                />
              )}

              <div className="d-flex justify-content-end gap-3 mt-3">
                <button className="btn btn-secondary" onClick={handleRejectCancel}>
                  Cancel
                </button>
                <button className="btn btn-danger" disabled={!selectedReason.trim()} onClick={handleRejectConfirm}>
                  Confirm
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
      {/* Scheduled Reminder Popup (OK Only) */}
      {showScheduledReminder && scheduledReminderOrder && (
        <div className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.6)", zIndex: 99999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-4 text-center rounded-4">

              <h5 className="fw-bold text-primary">
                Scheduled Order Reminder
              </h5>

              <p className="text-dark mt-2 mb-3">
                {scheduledReminderMessage}
              </p>

              <strong className="text-success d-block mb-3">
                Order {scheduledReminderOrder.orderId}
              </strong>

              <button
                className="btn btn-primary btn-lg rounded-pill px-5"
                onClick={() => {
                  setShowScheduledReminder(false);
                  setScheduledReminderOrder(null);
                  setScheduledReminderMessage("");
                }}
              >
                OK
              </button>

            </div>
          </div>
        </div>

      )}

      {/* Bank Details Missing Alert */}
      {
        showBankAlert && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10000 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-4 text-center rounded-4">
                <div className="mb-3">
                  <i className="material-symbols-outlined text-warning" style={{ fontSize: "48px" }}>
                    account_balance_wallet
                  </i>
                </div>
                <h4 className="fw-bold text-dark">Action Required</h4>
                <p className="text-secondary mt-2 mb-4">
                  You have received a new order, but you haven't added your bank details yet. <br />
                  Please add your bank details to process payments.
                </p>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={() => setShowBankAlert(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    onClick={() => {
                      setShowBankAlert(false);
                      navigate("/settlements/add");
                    }}
                  >
                    Add Bank Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

    </div >

  );
};

export default OrderModal;
